import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { Badge, Button, Checkbox, Input, Modal, Popover, Spinner } from "../../../shared";
import useOutsideClick from "../../../hooks/useOutsideClick";
import {
  useAddTagMutation,
  useGetTagsQuery,
} from "../../../redux/services/tags/tagsApi";
import { useUpdateAdminCustomerMutation } from "../../../redux/services/admin/adminApi";
import { TagBadgeList } from "../../../components";

const DEFAULT_COLOR = "#64748b";
const TAG_COLOR_PRESETS = [
  "#64748b",
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#22c55e",
  "#14b8a6",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
];

function normalizeTagId(value) {
  if (value == null || value === "") return "";
  const valueType = typeof value;
  if (valueType === "string" || valueType === "number") return String(value);
  if (valueType === "object") {
    if (Array.isArray(value)) return "";
    if (value.$oid != null) return String(value.$oid);
    if (value._id != null) return normalizeTagId(value._id);
    if (value.id != null) return normalizeTagId(value.id);
    if (typeof value.toString === "function") {
      try {
        const stringValue = value.toString();
        if (/^[a-f\d]{24}$/i.test(stringValue)) return stringValue;
      } catch (error) {
        return "";
      }
    }
  }
  return "";
}

const normalizeSelectedTagIds = (tags = []) =>
  Array.isArray(tags)
    ? tags.map((item) => normalizeTagId(item)).filter(Boolean)
    : [];

function buildTagMap(tags = []) {
  const map = new Map();
  tags.forEach((tag) => {
    const id = normalizeTagId(tag);
    if (id && tag && typeof tag === "object") map.set(id, tag);
  });
  return map;
}

function resolveTagsForDisplay(selectedIds, libraryTags, customerTags) {
  const libraryMap = buildTagMap(libraryTags);
  const populatedMap = buildTagMap(customerTags);
  return selectedIds
    .map((id) => libraryMap.get(id) || populatedMap.get(id))
    .filter((tag) => tag && typeof tag === "object");
}

const isHexColor = (value = "") => /^#[0-9a-fA-F]{6}$/.test(value);

const CustomerTagColorPicker = ({ value, onChange }) => {
  const selectedColor = isHexColor(value) ? value : DEFAULT_COLOR;

  return (
    <div>
      <div className="mb-1 text-sm font-medium text-gray-900">Color</div>
      <div className="flex flex-wrap gap-1.5">
        {TAG_COLOR_PRESETS.map((color) => {
          const isSelected = selectedColor.toLowerCase() === color.toLowerCase();
          return (
            <button
              key={color}
              type="button"
              aria-label={`Use ${color}`}
              className="h-7 w-7 rounded-full border border-gray-300 ring-offset-2"
              style={{
                backgroundColor: color,
                boxShadow: isSelected ? "0 0 0 2px #2563eb" : undefined,
              }}
              onClick={() => onChange(color)}
            />
          );
        })}
      </div>
    </div>
  );
};

export const CustomerTagsInline = ({
  customer = {},
  editable = true,
  onChangeTags,
  badgeSize = "sm",
  summarizeOverflow = false,
}) => {
  const [open, setOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [tagToRemove, setTagToRemove] = useState(null);
  const [removingTag, setRemovingTag] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(DEFAULT_COLOR);
  const ref = useRef(null);
  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
    strategy: "fixed",
    middleware: [
      offset(8),
      flip({ allowedPlacements: ["bottom-start", "bottom-end", "top-start", "top-end"] }),
      shift({ padding: 8 }),
    ],
  });
  const { data, isLoading } = useGetTagsQuery({
    appliesTo: "customers",
    limit: 200,
  });
  const [addTag, { isLoading: creating }] = useAddTagMutation();
  const [updateCustomer, { isLoading: saving }] = useUpdateAdminCustomerMutation();
  const tags = data?.tags || [];
  const selectedIds = normalizeSelectedTagIds(customer.tags);

  useOutsideClick(ref, () => setOpen(false));

  const selectedTags = useMemo(
    () => resolveTagsForDisplay(selectedIds, tags, customer.tags),
    [customer.tags, selectedIds, tags]
  );

  const { visibleTags, overflowTags } = useMemo(() => {
    if (!summarizeOverflow) {
      return { visibleTags: selectedTags, overflowTags: [] };
    }

    return {
      visibleTags: selectedTags.slice(0, 1),
      overflowTags: selectedTags.slice(1),
    };
  }, [selectedTags, summarizeOverflow]);

  useEffect(() => {
    if (!open) {
      setShowCreateForm(false);
      setNewTagName("");
      setNewTagColor(DEFAULT_COLOR);
    }
  }, [open]);

  async function saveTags(nextTagIds) {
    if (!customer?._id) return;

    if (typeof onChangeTags === "function") {
      await onChangeTags(nextTagIds);
      return;
    }

    await updateCustomer({
      id: customer._id,
      data: {
        tags: nextTagIds,
      },
    }).unwrap();
  }

  async function toggleTag(tag) {
    const tagId = normalizeTagId(tag);
    const nextTagIds = selectedIds.includes(tagId)
      ? selectedIds.filter((id) => id !== tagId)
      : Array.from(new Set([...selectedIds, tagId]));

    try {
      await saveTags(nextTagIds);
    } catch (error) {
      toast(error?.data?.message || "Failed to update customer tags", {
        type: "error",
        toastId: "customer-tags-update-error",
      });
    }
  }

  async function removeTagFromCustomer() {
    if (!tagToRemove) return;
    const tagId = normalizeTagId(tagToRemove);
    const nextTagIds = selectedIds.filter((id) => id !== tagId);

    try {
      setRemovingTag(true);
      await saveTags(nextTagIds);
      setTagToRemove(null);
    } catch (error) {
      toast(error?.data?.message || "Failed to remove customer tag", {
        type: "error",
        toastId: "customer-tags-remove-error",
      });
    } finally {
      setRemovingTag(false);
    }
  }

  async function createAndAssignTag() {
    const name = newTagName.trim();
    if (!name) {
      toast("Tag name is required", {
        type: "error",
        toastId: "customer-inline-tag-name-required",
      });
      return;
    }

    try {
      const response = await addTag({
        name,
        color: newTagColor || DEFAULT_COLOR,
        appliesTo: ["customers"],
      }).unwrap();
      const createdTagId = normalizeTagId(response?.tag);
      if (createdTagId) {
        await saveTags(Array.from(new Set([...selectedIds, createdTagId])));
      }
      setNewTagName("");
      setNewTagColor(DEFAULT_COLOR);
      setShowCreateForm(false);
    } catch (error) {
      toast(error?.data?.message || "Failed to create customer tag", {
        type: "error",
        toastId: "customer-inline-tag-create-error",
      });
    }
  }

  return (
    <div className="relative ml-2 flex min-w-0 items-center gap-1.5" ref={ref}>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <TagBadgeList
          tags={visibleTags}
          emptyText=""
          size={badgeSize}
          onRemoveTag={editable ? setTagToRemove : undefined}
        />
        {overflowTags.length > 0 && (
          <Popover
            position="top"
            offset={[-12, 0]}
            trigger={
              <Badge
                color="gray"
                label={`+${overflowTags.length}`}
                className="shrink-0 cursor-pointer hover:bg-gray-200"
              />
            }
          >
            <div className="space-y-1 p-2">
              {overflowTags.map((tag) => (
                <div
                  key={normalizeTagId(tag)}
                  className="whitespace-nowrap text-sm text-gray-800"
                >
                  {tag.name || tag.slug || "Tag"}
                </div>
              ))}
            </div>
          </Popover>
        )}
      </div>
      {editable && (
        <button
          ref={refs.setReference}
          type="button"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-gray-300 bg-white text-sm font-semibold leading-none text-gray-500 hover:border-primary-300 hover:text-primary-700"
          aria-label="Add customer tag"
          disabled={saving || creating}
          onClick={() => setOpen((prev) => !prev)}
        >
          +
        </button>
      )}

      {open && (
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            maxWidth: "calc(100vw - 1rem)",
            width: "20rem",
          }}
          className="z-[9999] rounded-xl border border-gray-200 bg-white p-3 shadow-lg"
        >
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-900">
              Customer Tags
            </div>
            <div className="flex items-center gap-2">
              {(saving || creating) && <Spinner size="sm" />}
              <Button
                size="xs"
                type={showCreateForm ? "primary" : "alternative"}
                disabled={creating || saving}
                onClick={() => setShowCreateForm((prev) => !prev)}
              >
                {showCreateForm ? "Hide" : "Create"}
              </Button>
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto border-b border-gray-100 pb-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Spinner />
              </div>
            ) : tags.length === 0 ? (
              <div className="text-sm text-gray-500">
                No customer tags yet. Create one to assign it to this customer.
              </div>
            ) : (
              <div className="space-y-2">
                {tags.map((tag) => (
                  <Checkbox
                    key={tag._id}
                    id={`inline-customer-tag-${tag._id}`}
                    label={tag.name}
                    checked={selectedIds.includes(normalizeTagId(tag))}
                    disabled={saving || creating}
                    onChange={() => toggleTag(tag)}
                  />
                ))}
              </div>
            )}
          </div>

          {showCreateForm && (
            <div className="mt-3 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <Input
                label="Tag Name"
                placeholder="New customer tag"
                value={newTagName}
                onChange={(target) => setNewTagName(target.value || "")}
              />
              <CustomerTagColorPicker
                value={newTagColor}
                onChange={setNewTagColor}
              />
              <Button
                size="sm"
                className="w-full"
                loading={creating || saving}
                disabled={creating || saving}
                onClick={createAndAssignTag}
              >
                Create and Add
              </Button>
            </div>
          )}
        </div>
      )}

      <Modal
        visible={!!tagToRemove}
        setVisible={(visible) => {
          if (!visible) setTagToRemove(null);
        }}
        title="Remove Tag"
        size="sm"
        content={
          <div className="text-sm text-gray-700">
            Remove{" "}
            <span className="font-semibold text-gray-900">
              {tagToRemove?.name || "this tag"}
            </span>{" "}
            from this customer?
          </div>
        }
        footer={
          <>
            <Button type="alternative" onClick={() => setTagToRemove(null)}>
              Cancel
            </Button>
            <Button
              type="danger"
              loading={removingTag || saving}
              disabled={removingTag || saving}
              onClick={removeTagFromCustomer}
            >
              Remove
            </Button>
          </>
        }
      />
    </div>
  );
};

export default CustomerTagsInline;
